import {
  differenceInDays,
  isBefore,
  isValid,
  parseISO,
  isAfter,
  differenceInYears,
  differenceInHours,
  addDays,
  format,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { AnyObject, string } from "yup";

import { POLICYHOLDER_MIN_AGE } from "@covertree/consts";

import { START_DATE_TIMEZONE } from "../time/getPolicyStartDate";
import { formatAwsDateString } from "../time/formatAwsDateString";

export const date = (fieldName?: string) =>
  string()
    .required(`${fieldName ?? "Date"} is required`)
    .test("dateValidation", "Invalid date format", (value) => {
      if (!value) return false;

      const dateArr = value.split("-");
      // if dateArr is longer than 3 elements, return false
      // this happens when the year is not provided
      if (dateArr.length !== 3) return false;
      // if any element of dateArr is "0", return false
      // this happens if user purposefully puts 0 in the date field
      if (dateArr.some((el) => el === "0")) return false;

      const parsedDate = parseISO(value);
      return isValid(parsedDate);
    });

export const coverageEffectiveDate = (
  fieldName: string,
  isBookConversionPolicy: boolean
) =>
  date().test(
    "coverageEffectiveDate",
    `${fieldName} cannot be earlier than today (ET time zone) or later than 30 days from today`,
    (value) => {
      if (!value) return true;
      if (isBookConversionPolicy) return true;
      const dateArr = value.split("-");
      if (dateArr.length !== 3) return false;
      if (dateArr.some((el) => el === "0")) return false;

      const parsedDate = parseISO(value);
      if (!isValid(parsedDate)) return false;

      const minNYDate = formatInTimeZone(
        new Date(),
        START_DATE_TIMEZONE,
        "yyyy-MM-dd'T'00:00:00"
      );

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore Type mismatch in addDays on build
      const maxNYDate = format(addDays(minNYDate, 30), "yyyy-MM-dd'T'00:00:00");

      const inputNYDate = formatInTimeZone(
        value,
        START_DATE_TIMEZONE,
        "yyyy-MM-dd'T'00:00:00"
      );

      const isBeforeMinDate = inputNYDate < minNYDate;
      const isAfterMaxDate = inputNYDate > maxNYDate;

      if (isBeforeMinDate || isAfterMaxDate) {
        return false;
      }

      return true;
    }
  );

export const sevenDayEffectiveDate = (
  fieldName: string,
  referenceDate: string,
  email: string
) => {
  const contactIATeamMessage = `If you require a different adjustment, please contact the IA team at ${email}`;

  return date()
    .test(
      "notBeforeUWReview",
      `The ${fieldName} cannot be earlier than ${formatAwsDateString(
        referenceDate
      )}. ${contactIATeamMessage}`,
      (value) => {
        const parsedDate = parseISO(value);
        const refDate = parseISO(referenceDate);

        const nyInputDate = formatInTimeZone(
          parsedDate,
          "America/New_York",
          "yyyy-MM-dd"
        );
        const nyRefDate = formatInTimeZone(
          refDate,
          "America/New_York",
          "yyyy-MM-dd"
        );

        const isBeforeReference = isBefore(
          parseISO(nyInputDate),
          parseISO(nyRefDate)
        );
        return !isBeforeReference;
      }
    )
    .test(
      "sevenDayEffectiveDate",
      `The ${fieldName} can only be changed by up to 7 days. ${contactIATeamMessage}`,
      (value) => {
        const parsedDate = parseISO(value);

        const nyInputDate = formatInTimeZone(
          parsedDate,
          "America/New_York",
          "yyyy-MM-dd"
        );
        const nyRefDate = formatInTimeZone(
          parseISO(referenceDate),
          "America/New_York",
          "yyyy-MM-dd"
        );

        const diffInDays = Math.abs(
          differenceInDays(parseISO(nyInputDate), parseISO(nyRefDate))
        );

        return diffInDays <= 7;
      }
    );
};

export const dob = () =>
  date().test(
    "birthDate",
    (value) => yupDOBMessage(value),
    (value) => {
      if (!value) return false;
      const dob = typeof value === "string" ? new Date(value) : value;
      return (
        isAfter(dob, Date.UTC(1910, 0, 0)) &&
        differenceInYears(new Date(), dob) >= POLICYHOLDER_MIN_AGE
      );
    }
  );

export const yupDOBMessage = (value: AnyObject) => {
  const birthDate = new Date(value?.value);

  if (!value || Number.isNaN(birthDate.getTime()))
    return "You must enter a birth date";
  if (differenceInHours(new Date(), new Date(birthDate)) < 0)
    return "Birth date cannot be in the future";
  if (differenceInYears(new Date(), new Date(birthDate)) < POLICYHOLDER_MIN_AGE)
    return `Minimum age is ${POLICYHOLDER_MIN_AGE} years`;
  if (!isAfter(new Date(birthDate), Date.UTC(1910, 0, 0)))
    return "Birth date should be after January 1, 1910";

  return "Invalid birth date";
};

export const nonFutureDateAndNotBeforeTwentyCentury = (fieldName: string) =>
  date(fieldName)
    .test(
      "future-date",
      `${fieldName} cannot be a date in the future`,
      (value) => {
        if (!value) return false;
        const dateArr = value.split("-");

        if (dateArr.length !== 3) return false;

        if (dateArr.some((el) => el === "0")) return false;

        return !isAfter(new Date(value), Date.now());
      }
    )
    .test(
      "before-twenty-century",
      `${fieldName} cannot be before 1900`,
      (value) => {
        if (!value) return false;
        const dateArr = value.split("-");

        if (dateArr.length !== 3) return false;

        if (dateArr.some((el) => el === "0")) return false;

        return !isBefore(new Date(value), new Date(1900, 0, 0));
      }
    );
